import { useState, useCallback } from 'react';
import { GlobalStyles } from '@mui/material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { KYCFlowController } from '../flows/KYCFlowController';
import type { KYCFlowState } from '../flows/KYCFlowController';
import { SimulationProvider, SIMULATION_SCENARIO } from '../contexts/SimulationContext';
import type { SimulationScenario } from '../contexts/SimulationContext';
import { StatsPage } from '../pages/StatsPage';
import type { KYCSession, KYCResult } from '../types/kyc';

// ─────────────────────────────────────────────────────────────────────────────
// Etiquetas legibles para cada escenario de simulación
// ─────────────────────────────────────────────────────────────────────────────
const SCENARIO_LABELS: Record<SimulationScenario, string> = {
  [SIMULATION_SCENARIO.ID_SUCCESS]: '✅ INE válida (sin comprobante)',
  [SIMULATION_SCENARIO.ID_REQUIRES_POA]: '📋 Pasaporte (requiere comprobante)',
  [SIMULATION_SCENARIO.ID_FAILURE]: '❌ Documento rechazado (score bajo)',
  [SIMULATION_SCENARIO.ID_EXPIRED]: '⏰ Documento vencido',
  [SIMULATION_SCENARIO.DATA_MISMATCH]: '🪪 Nombre no coincide (data_mismatch)',
  [SIMULATION_SCENARIO.POA_SUCCESS]: '✅ Comprobante válido',
  [SIMULATION_SCENARIO.POA_CONDITIONAL]: '⚠️ Comprobante condicional',
  [SIMULATION_SCENARIO.POA_FAILURE]: '❌ Comprobante rechazado',
};

const DEFAULT_BIRTH_DATE = '1985-03-15';
const DEFAULT_CURP = 'PEGJ850315HDFRNN01';

// ─────────────────────────────────────────────────────────────────────────────
// Personas de prueba — usuarios ficticios con nombres mexicanos reales.
// No tienen un propósito KYC específico: el escenario de simulación es
// independiente del usuario seleccionado.
// Los userId deben existir (o no) en kyc_users del sandbox según sea necesario.
// ─────────────────────────────────────────────────────────────────────────────
interface TestPersona {
  userId: string;
  nombres: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
}

const TEST_PERSONAS: TestPersona[] = [
  { userId: 'sim-user-001', nombres: 'Carlos Eduardo',    apellidoPaterno: 'Ramírez',    apellidoMaterno: 'Torres'    },
  { userId: 'sim-user-002', nombres: 'María Fernanda',    apellidoPaterno: 'Rodríguez',  apellidoMaterno: 'Vázquez'   },
  { userId: 'sim-user-003', nombres: 'José Luis',         apellidoPaterno: 'Hernández',  apellidoMaterno: 'Flores'    },
  { userId: 'sim-user-004', nombres: 'Ana Sofía',         apellidoPaterno: 'Martínez',   apellidoMaterno: 'Guerrero'  },
  { userId: 'sim-user-005', nombres: 'Roberto',           apellidoPaterno: 'García',     apellidoMaterno: 'Mendoza'   },
  { userId: 'sim-user-006', nombres: 'Valentina',         apellidoPaterno: 'López',      apellidoMaterno: 'Castillo'  },
];

const TEST_PERSONA_CUSTOM = '__custom__';

// Construye el nombre completo de una persona para mostrarlo en el selector
function buildFullName(persona: TestPersona): string {
  return `${persona.nombres} ${persona.apellidoPaterno} ${persona.apellidoMaterno}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────

// Modo desarrollo/testing: se muestra cuando la app NO está dentro de un iframe.
// Permite ejecutar el flujo KYC completo de forma local sin ningún backend real.
export function StandaloneMode() {
  // Vista activa: 'main' muestra el simulador KYC, 'stats' muestra el dashboard.
  // Permite arrancar directo en 'stats' cuando la URL trae ?view=stats — usado
  // por el botón Analytics del sandbox-landing.html
  const [view, setView] = useState<'main' | 'stats'>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('view') === 'stats' ? 'stats' : 'main';
  });

  // Recordamos si arrancamos en stats vía URL (desde sandbox-landing.html).
  // En ese caso el "Volver" del StatsPage debe regresar a la landing, no al
  // simulador. Si el usuario llegó al stats por click interno desde el botón
  // Analytics del modo desarrollo, "Volver" sí debe regresar al simulador.
  const [statsArrivedFromUrl] = useState<boolean>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('view') === 'stats';
  });

  const handleStatsBack = useCallback(() => {
    if (statsArrivedFromUrl) {
      window.location.href = '/sandbox-landing.html';
      return;
    }
    setView('main');
  }, [statsArrivedFromUrl]);

  // Persona seleccionada — '' sin persona, '__custom__' activa campos manuales
  const [selectedPersonaId, setSelectedPersonaId] = useState('');
  const [customUserId, setCustomUserId] = useState('');

  // Datos del jugador simulado — se auto-poblan al seleccionar una persona
  const [playerName, setPlayerName] = useState('');
  const [birthDate, setBirthDate] = useState(DEFAULT_BIRTH_DATE);
  const [curp, setCurp] = useState(DEFAULT_CURP);

  // Escenario de simulación seleccionado
  const [scenario, setScenario] = useState<SimulationScenario>(SIMULATION_SCENARIO.ID_SUCCESS);

  // Activa el paso de prueba de vida en el flujo simulado
  const [livenessEnabled, setLivenessEnabled] = useState(false);

  // Sesión activa — null significa que el flujo aún no empezó (o ya terminó y el usuario reinició)
  const [activeSession, setActiveSession] = useState<KYCSession | null>(null);

  // Resultado terminal del flujo (se mantiene aunque la sesión siga montada para mostrar debug)
  const [lastResult, setLastResult] = useState<KYCResult | null>(null);

  // Estado interno del reducer — se actualiza en tiempo real vía onStateChange
  const [debugState, setDebugState] = useState<KYCFlowState | null>(null);

  const handleScenarioChange = (e: SelectChangeEvent) => {
    setScenario(e.target.value as SimulationScenario);
  };

  // Cuando se selecciona una persona del selector, auto-pobla nombre y userId.
  // Cuando se elige 'sin persona' o 'personalizado', limpia los campos automáticos.
  const handlePersonaChange = (e: SelectChangeEvent) => {
    const personaId = e.target.value;
    setSelectedPersonaId(personaId);
    if (personaId && personaId !== TEST_PERSONA_CUSTOM) {
      const persona = TEST_PERSONAS.find((p) => p.userId === personaId);
      if (persona) {
        setPlayerName(buildFullName(persona));
      }
    }
  };

  // Resuelve el userId efectivo según la selección del selector de personas
  const resolvedUserId = (): string | undefined => {
    if (selectedPersonaId === TEST_PERSONA_CUSTOM) {
      return customUserId.trim() || undefined;
    }
    return selectedPersonaId || undefined;
  };

  const handleStart = () => {
    setLastResult(null);
    setDebugState(null);
    // Se construye una KYCSession con los datos del formulario de configuración.
    // D-7: si hay userId, el flujo inicia en CHECKING_STATUS para verificar el estado previo.
    setActiveSession({
      sessionId: `sim-${Date.now()}`,
      token: 'simulation-token',
      playerName: playerName.trim(),
      playerBirthDate: birthDate.trim(),
      playerCurp: curp.trim(),
      livenessEnabled,
      userId: resolvedUserId(),
    });
  };

  const handleReset = () => {
    setActiveSession(null);
    setLastResult(null);
    setDebugState(null);
  };

  // El flujo completó — se registra el resultado pero NO se desmonta el KYCFlowController
  // para que el usuario pueda ver la pantalla final (CompletionScreen / RejectionScreen)
  const handleComplete = (result: KYCResult) => {
    setLastResult(result);
  };

  // useCallback para estabilizar la referencia y no disparar el useEffect de KYCFlowController
  // en cada render del padre
  const handleStateChange = useCallback((state: KYCFlowState) => {
    setDebugState(state);
  }, []);

  const sessionActive = activeSession !== null;

  // Redirige a la página de estadísticas cuando el usuario lo solicita
  if (view === 'stats') {
    return <StatsPage onBack={handleStatsBack} />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: 'background.default',
      }}
    >
      <GlobalStyles styles={{
        '@keyframes kyc-pulse-dot': {
          '0%, 100%': { opacity: 1, transform: 'translateY(-50%) scale(1)' },
          '50%':       { opacity: 0.4, transform: 'translateY(-50%) scale(0.6)' },
        },
      }} />
      {/* Barra de herramientas de desarrollo */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar sx={{ gap: 2 }}>
          <Chip
            label="Modo Desarrollo"
            sx={{
              backgroundColor: '#7b1fa2',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
            size="small"
          />
          <Typography variant="subtitle2" color="text.secondary">
            Amarello KYC — Simulación local
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Box
            component="button"
            onClick={() => setView('stats')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.75,
              py: 0.6,
              border: '1px solid rgba(249,115,22,0.35)',
              borderRadius: 1.5,
              background: 'rgba(249,115,22,0.06)',
              color: '#f97316',
              cursor: 'pointer',
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontFamily: 'inherit',
              transition: 'all 0.2s ease',
              position: 'relative',
              '&:hover': {
                background: 'rgba(249,115,22,0.14)',
                borderColor: 'rgba(249,115,22,0.7)',
                boxShadow: '0 0 14px rgba(249,115,22,0.25)',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 5,
                height: 5,
                borderRadius: '50%',
                bgcolor: '#f97316',
                boxShadow: '0 0 6px #f97316',
                animation: 'kyc-pulse-dot 2s ease-in-out infinite',
              },
            }}
          >
            <BarChartIcon sx={{ fontSize: 15, ml: 1 }} />
            Analytics
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, flex: 1, pb: 4 }}>
        <Grid container spacing={3}>
          {/* ── Panel de configuración (izquierda) ── */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Configuración
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                {/* Selector de persona simulada */}
                <Typography variant="subtitle2" color="text.secondary">
                  Persona simulada
                </Typography>

                <FormControl size="small" fullWidth disabled={sessionActive}>
                  <InputLabel id="persona-label">Seleccionar persona</InputLabel>
                  <Select
                    labelId="persona-label"
                    value={selectedPersonaId}
                    label="Seleccionar persona"
                    onChange={handlePersonaChange}
                  >
                    <MenuItem value="">
                      <em>Sin persona — sin userId ni nombre predefinido</em>
                    </MenuItem>
                    {TEST_PERSONAS.map((persona) => (
                      <MenuItem key={persona.userId} value={persona.userId}>
                        {buildFullName(persona)}
                      </MenuItem>
                    ))}
                    <MenuItem value={TEST_PERSONA_CUSTOM}>Personalizada...</MenuItem>
                  </Select>
                </FormControl>

                {/* Input de userId solo visible cuando se selecciona "Personalizada..." */}
                {selectedPersonaId === TEST_PERSONA_CUSTOM && (
                  <TextField
                    label="userId personalizado"
                    value={customUserId}
                    onChange={(e) => setCustomUserId(e.target.value)}
                    size="small"
                    fullWidth
                    placeholder="Ej: user-abc123"
                    disabled={sessionActive}
                    helperText="Debe existir en kyc_users del sandbox o el flujo ignorará el estado"
                  />
                )}

                {/* Datos del jugador — se auto-poblan al elegir persona; editables manualmente */}
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  Datos del jugador
                </Typography>

                <TextField
                  label="Nombre completo"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={sessionActive}
                  placeholder="Se llena al seleccionar una persona"
                />

                <TextField
                  label="Fecha de nacimiento"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="YYYY-MM-DD"
                  disabled={sessionActive}
                />

                <TextField
                  label="CURP"
                  value={curp}
                  onChange={(e) => setCurp(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={sessionActive}
                />

                {/* Toggle prueba de vida */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={livenessEnabled}
                      onChange={(e) => setLivenessEnabled(e.target.checked)}
                      disabled={sessionActive}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Prueba de vida
                    </Typography>
                  }
                  sx={{ mt: 1 }}
                />

                {/* Selector de escenario */}
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  Escenario
                </Typography>

                <FormControl size="small" fullWidth disabled={sessionActive}>
                  <InputLabel id="scenario-label">Escenario de simulación</InputLabel>
                  <Select
                    labelId="scenario-label"
                    value={scenario}
                    label="Escenario de simulación"
                    onChange={handleScenarioChange}
                  >
                    {(Object.keys(SCENARIO_LABELS) as SimulationScenario[]).map((key) => (
                      <MenuItem key={key} value={key}>
                        {SCENARIO_LABELS[key]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Acciones */}
                <Button
                  variant="contained"
                  onClick={handleStart}
                  disabled={sessionActive || !playerName.trim()}
                  fullWidth
                  sx={{ mt: 1 }}
                >
                  Iniciar KYC
                </Button>

                {sessionActive && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleReset}
                    fullWidth
                  >
                    {lastResult ? 'Nueva sesión' : 'Reiniciar'}
                  </Button>
                )}
              </Box>

              {/* Indicador de resultado terminal */}
              {lastResult && (
                <Box sx={{ mt: 2 }}>
                  <Chip
                    label={`Estado final: ${lastResult.status.toUpperCase()}`}
                    color={
                      lastResult.status === 'completed'
                        ? 'success'
                        : lastResult.status === 'conditional'
                        ? 'warning'
                        : 'error'
                    }
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              )}
            </Paper>
          </Grid>

          {/* ── Panel del flujo KYC (derecha) ── */}
          <Grid item xs={12} md={8}>
            {!sessionActive ? (
              // Placeholder cuando no hay sesión activa
              <Box
                sx={{
                  height: 580,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <Typography variant="body1" color="text.disabled">
                  Configura los datos e inicia el KYC →
                </Typography>
              </Box>
            ) : (
              // Marco tipo pantalla de móvil para simular el iframe embebido
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Paper
                  elevation={3}
                  sx={{
                    width: '100%',
                    maxWidth: 420,
                    minHeight: 580,
                    borderRadius: 4,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* SimulationProvider envuelve el controlador para inyectar el contexto */}
                  <SimulationProvider scenario={scenario}>
                    <KYCFlowController
                      session={activeSession}
                      onComplete={handleComplete}
                      onStateChange={handleStateChange}
                    />
                  </SimulationProvider>
                </Paper>
              </Box>
            )}
          </Grid>

          {/* ── Panel de debug del estado del reducer (ancho completo) ── */}
          {sessionActive && debugState && (
            <Grid item xs={12}>
              <Accordion disableGutters elevation={1}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="caption" color="text.secondary">
                    Estado del reducer (debug)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    component="pre"
                    sx={{
                      fontSize: '0.75rem',
                      backgroundColor: '#f5f5f5',
                      p: 1.5,
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 300,
                      m: 0,
                    }}
                  >
                    {JSON.stringify(debugState, null, 2)}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
}
